import type { FunctionComponent, PropsWithChildren } from 'react'
import React, { Component, createContext, useContext } from 'react'
import { v4 } from 'uuid'

export type FallbackRenderProps<P> = P extends undefined
  ? { error: Error; componentStack: string; reset: () => void; ctx?: P }
  : { error: Error; componentStack: string; reset: () => void; ctx: P }
export type FallbackRender<P = unknown> = FunctionComponent<FallbackRenderProps<P>>

type State = { id: string; errorState: { error: Error; componentStack: string } | null }

export class ErrorBoundary<P> extends Component<PropsWithChildren<{ fallback: FallbackRender<P>; ctx?: P }>, State> {
  state: State = {
    id: 'initial',
    errorState: null,
  }

  componentDidCatch(error, { componentStack }: { componentStack: string }) {
    this.setState({ errorState: { error, componentStack } })
  }

  render() {
    if (this.state.errorState) {
      const Fallback = this.props.fallback as any
      const props = {
        componentStack: this.state.errorState.componentStack,
        ctx: this.props.ctx,
        error: this.state.errorState.error,
        reset: this.resetError,
      } as FallbackRenderProps<P>
      return <Fallback {...props} />
    }

    return <ErrorStateContext value={this.state}>{this.props.children}</ErrorStateContext>
  }

  resetError = () => {
    this.setState({ errorState: null, id: v4() })
  }
}

const ErrorStateContext = createContext<{ id: string } | null>(null)
export const useErrorBoundaryId = () => {
  return useContext(ErrorStateContext)?.id ?? 'initial'
}
